import { Button, Card, ScrollArea, Skeleton } from "@attmos/flowstack-ui";
import { useQuery } from "@tanstack/react-query";
import { Pause, Play } from "lucide-react";
import * as React from "react";

import { apiFetch } from "@/api/client";
import { EmptyState } from "@/components/layout/EmptyState";

interface LogLine {
  seq: number;
  at: string;
  level: string;
  message: string;
}

interface RunLogsResponse {
  lines: LogLine[];
  next_cursor: number | null;
}

/**
 * Logs panel — paginated log stream with follow-tail toggle.
 *
 * B1 ships an empty stub for the backend; the GUI surfaces the
 * "no structured logs yet" empty state cleanly. Once dispatch starts
 * emitting log lines, this component picks them up automatically
 * via the same /api/runs/<id>/logs endpoint.
 *
 * Per Rupert PR-259 r1 (blocker): polling is gated on isActive so a
 * Logs tab left open on a completed or failed run does not keep
 * hammering the endpoint forever — a terminal run never produces
 * new log lines. Parent passes isActive (computed from run.status).
 *
 * A failed run's terminal line carries VCM failure diagnostics (job id,
 * S3 root, controller stage/error, and a `task.log` tail when the
 * container ran) as `\n`-joined follow-on lines within `message` (#603).
 * Rendered here as an indented, visually-distinct block under the
 * `lifecycle: …` headline so a dev can see *why* a run failed without
 * leaving the portal.
 */
export function RunLogs({
  runId,
  isActive = false,
}: {
  runId: string;
  isActive?: boolean;
}): JSX.Element {
  const [followTail, setFollowTail] = React.useState(true);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  const { data, isLoading } = useQuery<RunLogsResponse>({
    queryKey: ["runs", "logs", runId],
    queryFn: async () => apiFetch<RunLogsResponse>(`/api/runs/${encodeURIComponent(runId)}/logs`),
    refetchInterval: isActive && followTail ? 5_000 : false,
  });

  // When new lines arrive in follow-tail mode, scroll to the bottom.
  React.useEffect(() => {
    if (!followTail || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [data?.lines.length, followTail]);

  return (
    <Card as="section">
      <header className="flex items-center justify-between border-b border-border-subtle px-4 py-2">
        <h2 className="text-sm font-medium">Logs</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setFollowTail((v) => !v)}
          aria-pressed={followTail}
        >
          {followTail ? (
            <>
              <Pause className="h-3.5 w-3.5" aria-hidden />
              Pause tail
            </>
          ) : (
            <>
              <Play className="h-3.5 w-3.5" aria-hidden />
              Follow tail
            </>
          )}
        </Button>
      </header>
      <ScrollArea className="logs-surface h-[400px] font-mono text-xs">
        <div ref={scrollRef} className="px-4 py-3">
          {isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-3 w-4/5" />
              <Skeleton className="h-3 w-3/5" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ) : !data || data.lines.length === 0 ? (
            <EmptyState
              title="No log lines yet."
              description="Logs appear here once the Tile body emits structured events. This panel polls automatically while the run is active."
              className="border-none bg-transparent"
            />
          ) : (
            data.lines.map((line) => {
              // On a failure, the backend appends the VCM diagnostics
              // (job id, S3 root, controller state) and — when the
              // container ran — a `task.log` tail as indented follow-on
              // lines after the primary `lifecycle: …` line (#603). Split
              // them out so the tab can set that block visually apart
              // instead of running it together with the headline as one
              // undifferentiated blob of text.
              const [headline, ...diagnosticLines] = line.message.split("\n");
              return (
                <div key={line.seq} className="flex gap-3 py-0.5">
                  <span className="shrink-0 text-muted-foreground">
                    {new Date(line.at).toLocaleTimeString()}
                  </span>
                  <span className="shrink-0 uppercase tracking-wider text-muted-foreground">
                    {line.level}
                  </span>
                  <span className="whitespace-pre-wrap break-all">
                    {headline}
                    {diagnosticLines.length > 0 ? (
                      <span className="mt-1 block border-l-2 border-border-subtle pl-2 text-muted-foreground">
                        {diagnosticLines.join("\n")}
                      </span>
                    ) : null}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </Card>
  );
}
